import { Pause, PlayArrow } from '@material-ui/icons'
import clsx from 'clsx'
import { Download } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { applyAvatarFallback, getSafeAvatarUrl } from '../../helpers/avatarFallback'
import { downloadFileFromUrl } from '../../utils/downloadFileFromUrl'
import { formatAudioDownloadName } from '../../utils/formatAudioDownloadName'
import { useStyles } from './style'

const AudioPlayer = ({
  audioUrl,
  profileImageUrl,
  isMessageRight,
  variant = 'default',
  showTime = true, // Nova prop: controla se os tempos serão exibidos
  fileName,
  ticket,
  message
}) => {
  const classes = useStyles({ variant })
  const audioRef = useRef(null)
  const waveformRef = useRef(null)

  const [isPlaying, setIsPlaying] = useState(false)
  const [duration, setDuration] = useState(0)
  const [currentTime, setCurrentTime] = useState(0)
  const [showRemainingTime, setShowRemainingTime] = useState(false) // UX: Alterna entre decorrido/restante

  // PERFORMANCE: useMemo evita que o array de barras seja recriado a cada renderização (como no `timeupdate`)
  const waveformBars = useMemo(() => {
    const barCount = variant === 'compact' ? 30 : 40 // Ajustado para preencher bem o espaço dinâmico
    const bars = []
    const heights = ['small', 'medium', 'tall']
    for (let i = 0; i < barCount; i++) {
      const heightClass = heights[Math.floor(Math.random() * heights.length)]
      bars.push(heightClass)
    }
    return bars
  }, [variant])

  // PERFORMANCE: useCallback evita a recriação da função
  const togglePlayPause = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()

    if (!audioRef.current) return

    if (isPlaying) {
      audioRef.current.pause()
    } else {
      audioRef.current.play()
    }
    setIsPlaying(prev => !prev)
  }, [isPlaying])

  const handleTimeUpdate = useCallback(() => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime)
    }
  }, [])

  const handleLoadedMetadata = useCallback(() => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration)
    }
  }, [])

  const handleWaveformClick = useCallback((e) => {
    if (!waveformRef.current || !audioRef.current || duration === 0) return

    const rect = waveformRef.current.getBoundingClientRect()
    const clickPosition = (e.clientX - rect.left) / rect.width

    audioRef.current.currentTime = clickPosition * duration
  }, [duration])

  // ACESSIBILIDADE: Suporte a teclado para o waveform
  const handleWaveformKeyDown = useCallback((e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      togglePlayPause(e)
    }
  }, [togglePlayPause])

  const handleDownload = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    const preferredName =
      ticket && message ? formatAudioDownloadName(ticket, message) : fileName
    downloadFileFromUrl(audioUrl, preferredName)
  }, [audioUrl, fileName, ticket, message])

  const formatTime = (seconds) => {
    if (isNaN(seconds) || seconds < 0) return '0:00'
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`
  }

  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return

    const onEnded = () => setIsPlaying(false)

    audio.addEventListener('timeupdate', handleTimeUpdate)
    audio.addEventListener('loadedmetadata', handleLoadedMetadata)
    audio.addEventListener('ended', onEnded)

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate)
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata)
      audio.removeEventListener('ended', onEnded)
      audio.pause() // Evita memory leak ou áudio tocando em background ao desmontar
    }
  }, [handleTimeUpdate, handleLoadedMetadata])

  const progressPercentage = duration > 0 ? (currentTime / duration) * 100 : 0

  // Calcula o tempo restante
  const remainingTime = duration - currentTime

  return (
    <div className={classes.audioWrapper}>
      <div
        className={clsx(classes.audioPlayer, {
          [classes.audioPlayerRight]: isMessageRight,
          [classes.compactPlayer]: variant === 'compact'
        })}
      >
        {variant !== 'compact' && !isMessageRight && profileImageUrl && (
          <img
            src={getSafeAvatarUrl(profileImageUrl)}
            onError={applyAvatarFallback}
            referrerPolicy="no-referrer"
            alt="Foto de perfil" // ACESSIBILIDADE: Texto descritivo
            className={classes.profileImage}
          />
        )}

        <button
          className={classes.playButton}
          onClick={togglePlayPause}
          type="button"
          aria-label={isPlaying ? 'Pausar' : 'Tocar'}
        >
          {isPlaying ? (
            <Pause style={{ fontSize: 28 }} />
          ) : (
            <PlayArrow style={{ fontSize: 28 }} />
          )}
        </button>

        <div className={classes.playerControls}>
          {/* Exibição condicional do tempo controlada pela prop showTime */}
          {showTime && (
            <div
              className={classes.timeDisplay}
              aria-live="polite"
              onClick={() => setShowRemainingTime(!showRemainingTime)}
              style={{ cursor: 'pointer' }}
              title="Clique para alternar o formato de tempo"
            >
              {/* UX: Se o usuário clicar, alterna entre tempo atual e restante */}
              {showRemainingTime
                ? `-${formatTime(remainingTime)}`
                : formatTime(currentTime)
              } / {formatTime(duration)}
            </div>
          )}

          <div
            className={classes.waveformContainer}
            onClick={handleWaveformClick}
            onKeyDown={handleWaveformKeyDown}
            ref={waveformRef}
            role="slider"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(progressPercentage)}
            tabIndex={0} // ACESSIBILIDADE: Focável por teclado
          >
            {waveformBars.map((heightClass, index) => (
              <div
                key={index}
                className={clsx(classes.waveBar, heightClass, {
                  played: (index / waveformBars.length) * 100 <= progressPercentage
                })}
              />
            ))}
          </div>
        </div>

        <audio
          ref={audioRef}
          src={audioUrl}
          preload="metadata"
          style={{ display: 'none' }}
        />

        {variant !== 'compact' && (
          <button
            className={classes.downloadButton}
            onClick={handleDownload}
            type="button"
            aria-label="Baixar áudio"
            title="Baixar áudio"
          >
            <Download size={18} />
          </button>
        )}
      </div>
    </div>
  )
}

export default AudioPlayer